import { Directive, ElementRef, Input, OnDestroy, OnInit } from '@angular/core';
import { FiBrowserEventsService } from '@fi-shared/fi-shared-services/fi-browser-events.service';
import { interval, Subject } from 'rxjs';
import { debounce, takeUntil, throttle } from 'rxjs/operators';

enum FiAutoscrollOnDragDirection {
    None = 0,
    Top = 1,
    Bottom = 2,
}

@Directive({
    selector: '[fiAutoscrollOnDrag]',
    standalone: false,
})
export class FiAutoscrollOnDragDirective implements OnInit, OnDestroy {
    @Input() fiAutoscrollOnDragMargin = 100;
    @Input() fiAutoscrollOnDragSpeed = 400;

    // ngx-drag-drop invokes preventDefault() on window dragstart and dragend event, so we have to pass this event manually
    private timer: NodeJS.Timeout;
    private rect: DOMRect;
    private onDrag$ = new Subject<void>();

    constructor(
        private elRef: ElementRef<HTMLElement>,
        private fiBrowserEventsService: FiBrowserEventsService,
    ) {}

    ngOnInit(): void {
        let scrollDirection = FiAutoscrollOnDragDirection.None;

        // we should determine when drag starts and ends
        this.onDrag$
            .pipe(debounce((val) => interval(200)))
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe(() => {
                this.clearTimer();
                this.rect = null;
            });
        this.fiBrowserEventsService
            .getWindowEventSubject$('drag')
            .pipe(
                throttle((val) => interval(100)),
                takeUntil(this.ngOnDestroy$),
            )
            .subscribe((event: DragEvent) => {
                this.onDrag$.next();
                if (!this.rect) {
                    // new dragging session
                    this.rect = this.elRef.nativeElement.getBoundingClientRect();
                }

                if (event.y - this.fiAutoscrollOnDragMargin < this.rect.top) {
                    scrollDirection = FiAutoscrollOnDragDirection.Top;
                } else if (event.y + this.fiAutoscrollOnDragMargin > this.rect.bottom) {
                    scrollDirection = FiAutoscrollOnDragDirection.Bottom;
                } else {
                    scrollDirection = FiAutoscrollOnDragDirection.None;
                    this.clearTimer();
                }

                if (scrollDirection != FiAutoscrollOnDragDirection.None && !this.timer) {
                    this.timer = setInterval(() => {
                        if (scrollDirection === FiAutoscrollOnDragDirection.Top) {
                            const scrollTop = this.elRef.nativeElement.scrollTop;
                            this.elRef.nativeElement.scrollTop = scrollTop - this.fiAutoscrollOnDragSpeed / 40;
                        } else if (scrollDirection === FiAutoscrollOnDragDirection.Bottom) {
                            const scrollTop = this.elRef.nativeElement.scrollTop;
                            this.elRef.nativeElement.scrollTop = scrollTop + this.fiAutoscrollOnDragSpeed / 40;
                        }
                    }, 25);
                }
            });
    }

    clearTimer() {
        if (this.timer) {
            clearInterval(this.timer);
            this.timer = undefined;
        }
    }

    ngOnDestroy$ = new Subject<void>();
    ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
        this.onDrag$.complete();
    }
}
